import React, { useEffect, useRef } from "react"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { useLocation } from "wouter"

gsap.registerPlugin(ScrollTrigger)

const VALUES_PATH =
  "M -30,520 " +
  "C 30,520 55,534 80,535 " +
  "C 140,536 210,490 250,388 " +
  "C 290,282 362,208 402,244 " +
  "C 442,282 392,412 308,512 " +
  "C 254,574 172,568 132,504 " +
  "C 98,448 188,302 325,276 " +
  "C 356,266 372,388 374,528 " +
  "C 373,598 373,644 373,648 " +
  "C 378,628 452,620 540,619 " +
  "C 612,618 662,618 697,619 " +
  "C 730,619 784,568 820,458 " +
  "C 858,336 876,228 922,226 " +
  "C 972,224 986,332 946,432 " +
  "C 910,512 838,530 800,478 " +
  "C 764,420 800,312 854,294 " +
  "C 962,272 1152,368 1312,411 " +
  "C 1372,424 1432,418 1472,415"

const VALUES = [
  { text: "active listening",       left: "4.8%",  top: "59.6%" },
  { text: "continuous improvement", left: "22.6%", top: "30.7%" },
  { text: "change readiness",       left: "25.9%", top: "72%" },
  { text: "continuous learning",    left: "48.4%", top: "68.7%" },
  { text: "continuous building",    left: "59.3%", top: "32.7%" },
  { text: "sharing",                left: "91.1%", top: "45.7%" },
]

const PHRASES = [
  { text: "Innovation", left: "9.4%",  top: "14.7%" },
  { text: "is not",     left: "60.2%", top: "31.6%" },
  { text: "a linear",   left: "46.0%", top: "49.2%" },
  { text: "journey",    left: "27.8%", top: "65.6%" },
]

export function About() {
  const [, navigate] = useLocation()
  const valuesRef = useRef()
  const pathRef = useRef()
  const labelRefs = useRef([])
  const quoteRef = useRef()
  const phraseRefs = useRef([])
  const introRef = useRef()
  const rowRefs = useRef([])
  const presentRef = useRef()
  const futureRef = useRef()
  const ROWS = 15
  const CENTER = 7

  useEffect(() => {
    document.body.style.backgroundColor = ""
    document.body.style.color = ""

    // Set transformPerspective on each phrase for 3D hover
    phraseRefs.current.forEach((el) => {
      if (el) gsap.set(el, { transformPerspective: 800 })
    })

    const ctx = gsap.context(() => {
      // ── Values section ──────────────────────────────────────────
      const path = pathRef.current
      const pathLen = path.getTotalLength()
      gsap.set(path, { strokeDasharray: pathLen, strokeDashoffset: pathLen })
      gsap.set(labelRefs.current, { opacity: 0, y: 36 })

      const valuesTl = gsap.timeline()
      labelRefs.current.forEach((label) => {
        valuesTl.to(label, { opacity: 1, y: 0, duration: 0.5, ease: "power3.out" }, "+=0.1")
      })
      valuesTl.to(path, { strokeDashoffset: 0, duration: 2, ease: "power2.inOut" }, "+=0.2")

      ScrollTrigger.create({
        trigger: valuesRef.current,
        start: "top top",
        end: () => `+=${window.innerHeight * 3}`,
        pin: true,
        pinSpacing: true,
        scrub: 0.8,
        animation: valuesTl,
        zIndex: 0,
      })

      // ── Quote section ─────────────────────────────────────────────
      gsap.set(".quote-char", { opacity: 0, y: -50 })

      const quoteTl = gsap.timeline()
      phraseRefs.current.forEach((phrase) => {
        if (!phrase) return
        const chars = phrase.querySelectorAll(".quote-char")
        quoteTl.to(
          chars,
          { opacity: 1, y: 0, duration: 0.6, ease: "power3.out", stagger: 0.03 },
          "+=0.15"
        )
      })

      ScrollTrigger.create({
        trigger: quoteRef.current,
        start: "top top",
        end: () => `+=${window.innerHeight * 2.5}`,
        pin: true,
        pinSpacing: true,
        scrub: 0.8,
        animation: quoteTl,
        zIndex: 0,
      })

      // ── Fill section ──────────────────────────────────────────────
      const otherRows = rowRefs.current.filter((_, i) => i !== CENTER)
      gsap.set(otherRows, { opacity: 0 })

      const presentChars = presentRef.current.querySelectorAll(".about-char")
      const futureChars = futureRef.current.querySelectorAll(".about-char")
      gsap.set([presentChars, futureChars], { opacity: 0, y: 18 })

      const tl = gsap.timeline()

      tl.to(presentChars, { opacity: 1, y: 0, duration: 0.4, ease: "power3.out", stagger: 0.04 })
      tl.to(futureChars, { opacity: 1, y: 0, duration: 0.4, ease: "power3.out", stagger: 0.04 }, "<0.2")

      for (let dist = 1; dist <= 7; dist++) {
        const targets = [rowRefs.current[CENTER - dist], rowRefs.current[CENTER + dist]].filter(Boolean)
        tl.to(targets, { opacity: 1, duration: 0.25, ease: "power2.out" }, "+=0.15")
      }

      tl.to(".about-fill-word", { color: "#FF4000", duration: 0.5, ease: "power2.out" }, "+=0.3")

      ScrollTrigger.create({
        trigger: introRef.current,
        start: "top top",
        end: () => `+=${window.innerHeight * 7}`,
        pin: true,
        pinSpacing: true,
        scrub: 1,
        animation: tl,
        zIndex: 0,
      })

      ScrollTrigger.refresh()
    })

    // ── Hover pivot ───────────────────────────────────────────────
    const removeHover = phraseRefs.current.map((el) => {
      if (!el) return () => {}

      const onMove = (e) => {
        const r = el.getBoundingClientRect()
        const nx = ((e.clientX - r.left) / r.width  - 0.5) * 2
        const ny = ((e.clientY - r.top)  / r.height - 0.5) * 2
        gsap.to(el, { rotateY: nx * 8, rotateX: -ny * 5, duration: 0.5, ease: "power2.out", overwrite: "auto" })
      }
      const onLeave = () => {
        gsap.to(el, { rotateY: 0, rotateX: 0, duration: 0.6, ease: "power2.out", overwrite: "auto" })
      }

      el.addEventListener("mousemove", onMove)
      el.addEventListener("mouseleave", onLeave)
      return () => {
        el.removeEventListener("mousemove", onMove)
        el.removeEventListener("mouseleave", onLeave)
      }
    })

    return () => {
      ctx.revert()
      removeHover.forEach((fn) => fn())
    }
  }, [])

  return (
    <div className="about-page">

      {/* Values section */}
      <div ref={valuesRef} className="about-values">
        <svg className="about-values-line" viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">
          <path ref={pathRef} d={VALUES_PATH} fill="none" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        {VALUES.map((v, i) => (
          <span key={i} ref={el => (labelRefs.current[i] = el)} className="about-values-label" style={{ left: v.left, top: v.top }}>
            {v.text}
          </span>
        ))}
      </div>

      {/* Quote section */}
      <div ref={quoteRef} className="about-quote">
        <div className="about-quote-scene">
          {PHRASES.map((phrase, pi) => (
            <div
              key={pi}
              ref={el => (phraseRefs.current[pi] = el)}
              className="about-quote-phrase"
              style={{ left: phrase.left, top: phrase.top }}
            >
              {phrase.text.split("").map((char, ci) => (
                <span key={ci} className="quote-char">{char === " " ? "\u00a0" : char}</span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Fill animation */}
      <div ref={introRef} className="about-intro">
        <div className="about-fill-scene">
          {Array.from({ length: ROWS }).map((_, ri) => (
            <div key={ri} ref={el => (rowRefs.current[ri] = el)} className="about-fill-row">
              <div ref={ri === CENTER ? presentRef : null} className="about-fill-word">
                {ri === CENTER
                  ? "Present".split("").map((char, i) => <span key={i} className="about-char">{char}</span>)
                  : "Present"
                }
              </div>
              <div ref={ri === CENTER ? futureRef : null} className="about-fill-word">
                {ri === CENTER
                  ? "Future".split("").map((char, i) => <span key={i} className="about-char">{char}</span>)
                  : "Future"
                }
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* About text */}
      <div className="inner-page">
        <div className="inner-page-content">
          <p className="eyebrow">About us</p>
          <h1 className="inner-page-headline">Present Future.</h1>
          <div className="about-body">
            <p>
              Present Future was founded by Luca, a product director with 18 years in tech. Having seen
              the pattern repeat — great products held back by leadership that hasn't caught up, and
              brilliant leaders building the wrong things — he built something different.
            </p>
            <p>
              A practice that works on two souls: the person and the product. The team and the vision.
              The now and the next. Grounded in the belief that breakthrough outcomes come when these
              usually separate domains move together.
            </p>
            <p>That's why we're called Present Future.</p>
          </div>
        </div>
      </div>
    </div>
  )
}
